/**
 * AD/Entra Phase 3 — service surface for the admin UI.
 *
 * Phase 1 + 2 deliver the snapshot ingestion via the ad-sync-fetch
 * edge function. Phase 3 adds the diff/apply contract:
 *
 *   - compute_ad_sync_proposals(run_id)        — produces proposed actions
 *   - apply_ad_sync_action(action_id)          — single apply
 *   - apply_ad_sync_actions_batch(uuid[])      — bulk apply w/ per-row isolation
 *   - reject_ad_sync_action(action_id, reason) — mark rejected
 */

import { supabase } from "./supabase";
import type {
  AdSyncAction,
  AdSyncProposalFilters,
  AdSyncRun,
  ApplyActionResult,
  BatchApplyResult,
  ComputeProposalsResult,
} from "../types/adSync.types";

export async function listAdSyncRuns(limit = 25): Promise<AdSyncRun[]> {
  const { data, error } = await supabase
    .from("ad_sync_runs")
    .select("*")
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) {
    throw error;
  }
  return (data ?? []) as AdSyncRun[];
}

export async function getAdSyncRun(runId: string): Promise<AdSyncRun | null> {
  const { data, error } = await supabase
    .from("ad_sync_runs")
    .select("*")
    .eq("id", runId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return data as AdSyncRun | null;
}

export async function listAdSyncProposals(
  runId: string,
  filters: AdSyncProposalFilters = {},
): Promise<AdSyncAction[]> {
  // PostgREST caps a response at max_rows (1000). A run can hold more than
  // that, and "Select all" must count every proposal, so read in pages with a
  // stable order (id is the tiebreak) until a short page comes back.
  const PAGE = 1000;
  const out: AdSyncAction[] = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase
      .from("ad_sync_actions")
      .select("*")
      .eq("run_id", runId)
      .order("action_type", { ascending: true })
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (filters.status) {
      q = q.eq("status", filters.status);
    }
    if (filters.action_type) {
      q = q.eq("action_type", filters.action_type);
    }
    if (filters.flag) {
      q = q.contains("flags", [filters.flag]);
    }
    const { data, error } = await q;
    if (error) {
      throw error;
    }
    const rows = (data ?? []) as AdSyncAction[];
    out.push(...rows);
    if (rows.length < PAGE) {
      return out;
    }
  }
}

/**
 * Trigger a fresh Entra sync via the public edge function. Returns the
 * new run_id on success; the caller can poll the run row for status
 * transitions running → complete.
 */
export async function triggerAdSyncFetch(): Promise<{
  run_id?: string;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke("ad-sync-fetch", {
    body: {},
  });
  if (error) {
    return { error: error.message ?? "Failed to trigger ad-sync-fetch" };
  }
  return data ?? {};
}

export async function computeAdSyncProposals(
  runId: string,
): Promise<ComputeProposalsResult> {
  const { data, error } = await supabase.rpc("compute_ad_sync_proposals", {
    p_run_id: runId,
  });
  if (error) {
    throw error;
  }
  return data as ComputeProposalsResult;
}

export async function applyAdSyncAction(
  actionId: string,
): Promise<ApplyActionResult> {
  const { data, error } = await supabase.rpc("apply_ad_sync_action", {
    p_action_id: actionId,
  });
  if (error) {
    throw error;
  }
  return data as ApplyActionResult;
}

export async function rejectAdSyncAction(
  actionId: string,
  reason: string,
): Promise<{ status: "rejected"; action_id: string }> {
  const { data, error } = await supabase.rpc("reject_ad_sync_action", {
    p_action_id: actionId,
    p_reason: reason,
  });
  if (error) {
    throw error;
  }
  return data as { status: "rejected"; action_id: string };
}

export async function applyAdSyncActionsBatch(
  actionIds: string[],
): Promise<BatchApplyResult> {
  const { data, error } = await supabase.rpc("apply_ad_sync_actions_batch", {
    p_action_ids: actionIds,
  });
  if (error) {
    throw error;
  }
  return data as BatchApplyResult;
}

/** Ids per apply_ad_sync_actions_batch call (one database transaction each). */
export const AD_SYNC_APPLY_CHUNK_SIZE = 100;

/**
 * Order the apply must follow: a create has to be applied before the
 * person/reports-to proposals that depend on it, or those fail with
 * WAITING_ON_DEPENDENCY. Every dependency the engine emits points at a
 * create_sales_position, so creates go first.
 */
const APPLY_TYPE_ORDER: Record<string, number> = {
  create_sales_position: 0,
  link_profile_azure_ad_user_id: 1,
  update_sales_position_person: 2,
  update_sales_position_parent: 3,
  vacate_sales_position: 4,
  update_sales_position_attribute: 5,
};

export interface ChunkedApplyResult extends BatchApplyResult {
  /** Ids never sent because an earlier chunk's call itself failed. */
  not_attempted: string[];
  /** The error that stopped the run, when a whole chunk call failed. */
  stopped_error?: string;
}

/**
 * Apply many proposals through the existing apply_ad_sync_actions_batch RPC
 * (same per-item checks as the single Apply button: admin role, status must
 * still be "proposed", dependency applied, stale-state supersede, guards),
 * in chunks of AD_SYNC_APPLY_CHUNK_SIZE, sent one after another in
 * dependency order. Per-item failures come back in `results`; if a whole
 * chunk call fails (network, timeout), the run stops and the remaining ids
 * are reported as not attempted rather than silently dropped.
 */
export async function applyAdSyncActionsInChunks(
  actions: Array<Pick<AdSyncAction, "id" | "action_type" | "created_at">>,
  onProgress?: (done: number, total: number) => void,
): Promise<ChunkedApplyResult> {
  const ordered = [...actions].sort((a, b) => {
    const t =
      (APPLY_TYPE_ORDER[a.action_type] ?? 9) -
      (APPLY_TYPE_ORDER[b.action_type] ?? 9);
    if (t !== 0) {
      return t;
    }
    return a.created_at.localeCompare(b.created_at);
  });
  const ids = ordered.map((a) => a.id);
  const total: ChunkedApplyResult = {
    applied: 0,
    superseded: 0,
    failed: 0,
    results: [],
    not_attempted: [],
  };
  onProgress?.(0, ids.length);
  for (let i = 0; i < ids.length; i += AD_SYNC_APPLY_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + AD_SYNC_APPLY_CHUNK_SIZE);
    try {
      const r = await applyAdSyncActionsBatch(chunk);
      total.applied += r.applied;
      total.superseded += r.superseded;
      total.failed += r.failed;
      total.results.push(...(r.results ?? []));
    } catch (e: unknown) {
      total.not_attempted = ids.slice(i);
      total.stopped_error =
        typeof e === "object" && e !== null && "message" in e
          ? String((e as { message: unknown }).message ?? "")
          : String(e);
      return total;
    }
    onProgress?.(Math.min(i + chunk.length, ids.length), ids.length);
  }
  return total;
}

/**
 * The batch-apply RPC catches per-row exceptions as `failed` rows in the
 * results array. Some failures are transient (WAITING_ON_DEPENDENCY when
 * a dependent create hasn't been applied yet) and the operator should
 * retry after applying the dependency. This helper classifies the error
 * so the UI can surface "Apply dependent create first, then retry" copy.
 */
export function isWaitingOnDependencyError(message?: string): boolean {
  if (!message) {
    return false;
  }
  return /WAITING_ON_DEPENDENCY/i.test(message);
}

/** An RM / TM proposal and its territory (mig 1243, get_ad_sync_territory_needs). */
export interface AdSyncTerritoryNeed {
  action_id: string;
  action_type: string;
  position_type: "regional_manager" | "territory_manager";
  position_id: string | null;
  current_states: string[] | null;
  picked_states: string[] | null;
}

/** The proposed actions of a run that put someone into an RM / TM position (territory required to apply). */
export async function getAdSyncTerritoryNeeds(
  runId: string,
): Promise<AdSyncTerritoryNeed[]> {
  const { data, error } = await supabase.rpc("get_ad_sync_territory_needs", {
    p_run_id: runId,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as AdSyncTerritoryNeed[];
}

/** Store the territory (US state codes) on one proposed RM / TM action; returns the normalized list. */
export async function setAdSyncActionTerritory(
  actionId: string,
  states: string[],
): Promise<string[]> {
  const { data, error } = await supabase.rpc("set_ad_sync_action_territory", {
    p_action_id: actionId,
    p_states: states,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as string[];
}

/** The territory a proposal will apply with: the pick on the action, else the position's current states. */
export function effectiveTerritory(need: AdSyncTerritoryNeed): string[] {
  if (need.picked_states && need.picked_states.length > 0) {
    return need.picked_states;
  }
  return need.current_states ?? [];
}
